import { FC, ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import AsyncSelect from 'react-select/async';
import { StylesConfig } from 'react-select';
import { useNetworkService } from '@edikk-edikk/di-container';
import { CityForSelectType, FindCityResponseType } from '#home/types';
import { FIND_CITY_URL } from '#home/constants';

type PropsType = {
  id: string;
  styles: StylesConfig;
  placeholder: ReactNode;
  onChange: (selectedCity: CityForSelectType) => void;
  defaultValue?: CityForSelectType|null;
};

const CityChooser: FC<PropsType> = ({
  id,
  styles,
  placeholder,
  onChange,
  defaultValue = null,
}) => {
  const networkService = useNetworkService();

  const loadOptions = (search: string) => new Promise<CityForSelectType[]>((resolve) => {
    networkService.get<FindCityResponseType>(FIND_CITY_URL, {
      params: {
        search,
      },
    }).then((response) => {
      resolve(response.data.cityList);
    });
  });

  const noOptionsMessageWithFilter = useMemo<ReactNode>(() => <FormattedMessage defaultMessage="No cities" />, []);
  const noOptionsMessageWithoutFilter = useMemo<ReactNode>(() => <FormattedMessage defaultMessage="Start typing" />, []);
  const noOptionsMessage = useMemo<(inputValue: string) => ReactNode>(() => (inputValue: string) => {
    if (inputValue) {
      return noOptionsMessageWithFilter;
    }
    return noOptionsMessageWithoutFilter;
  }, [noOptionsMessageWithFilter, noOptionsMessageWithoutFilter]);

  return (
    <AsyncSelect
      instanceId={id}
      className={id}
      loadOptions={loadOptions}
      menuPlacement="top"
      defaultValue={defaultValue}
      onChange={onChange}
      components={{
        IndicatorSeparator: null,
        DropdownIndicator: null,
      }}
      noOptionsMessage={({ inputValue }) => noOptionsMessage(inputValue)}
      placeholder={placeholder}
      aria-label="Find location"
      styles={styles}
    />
  );
};

export { CityChooser };
